import { createContext } from "react";
import type { TooltipSections } from "@design-system/Tooltip/Tooltip.types";
import type { DataGridCellTooltip } from "../columns/types";

/** What the shared tooltip draws for one target. */
export interface CellTooltipBody {
    content?: string;
    title?: string;
    sections?: TooltipSections;
    bullets?: readonly string[];
}

export const toCellTooltipBody = (
    tooltip: DataGridCellTooltip | undefined,
): CellTooltipBody => {
    if (tooltip === undefined) {
        return {};
    }
    return typeof tooltip === "string" ? { content: tooltip } : tooltip;
};

/**
 * The structured tooltips of a table's `CellTooltipTarget`s, by element. The
 * host reads it for targets marked `data-cell-tooltip-rich` and hears when
 * one changes, so an open tooltip follows its row.
 */
export interface CellTooltipRegistry {
    get: (element: HTMLElement) => DataGridCellTooltip | undefined;
    set: (element: HTMLElement, tooltip: DataGridCellTooltip) => void;
    delete: (element: HTMLElement) => void;
    /** One listener at a time: the host's. Returns the unsubscribe. */
    watch: (onChange: (element: HTMLElement) => void) => () => void;
}

export const createCellTooltipRegistry = (): CellTooltipRegistry => {
    const tooltips = new WeakMap<HTMLElement, DataGridCellTooltip>();
    let notify: ((element: HTMLElement) => void) | undefined;
    return {
        get: (element) => tooltips.get(element),
        set: (element, tooltip) => {
            tooltips.set(element, tooltip);
            notify?.(element);
        },
        delete: (element) => {
            tooltips.delete(element);
        },
        watch: (onChange) => {
            notify = onChange;
            return () => {
                notify = undefined;
            };
        },
    };
};

/** The host's registry; `null` outside a `CellTooltipHost`. */
export const CellTooltipRegistryContext =
    createContext<CellTooltipRegistry | null>(null);
